import React,{useState,useEffect} from 'react'
import * as Yup from 'yup'
import { DescriptionInput, TextInputComponent, WearableOptionsCompoent } from '../../../common/Form/Input.component'
import { useForm,useFieldArray, Controller, useWatch } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useListAllQuery } from '../../../api/collection.api'
import LoadingComponent from '../../../common/Loading/Loading.component'
import Select from "react-select"
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    rectSortingStrategy,
} from '@dnd-kit/sortable'
import {
    useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities';

// Sortable Media Item Component
const SortableMediaItem = ({ mediaItem, index, onRemove }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: `media-${index}` });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    // Handle different data formats
    let mediaUrl, mediaType;
    
    if (mediaItem instanceof File || mediaItem instanceof Blob) {
        mediaUrl = URL.createObjectURL(mediaItem);
        mediaType = mediaItem.type.startsWith('image/') ? 'image' : 'video';
    } else if (typeof mediaItem === 'string') {
        // Legacy format - assume it's an image URL
        mediaUrl = mediaItem;
        mediaType = 'image';
    } else if (typeof mediaItem === 'object' && mediaItem.url) {
        // New format with type
        mediaUrl = mediaItem.url;
        mediaType = mediaItem.type || 'image';
    } else {
        mediaType = 'unknown';
    }

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            className="media-item-sortable"
            data-index={index}
        >
            <div style={{ 
                display: "inline-block", 
                margin: "5px", 
                position: "relative", 
                border: isDragging ? "2px dashed #007bff" : "1px solid #ddd", 
                borderRadius: "8px", 
                overflow: "hidden",
                cursor: isDragging ? "grabbing" : "grab",
                transform: isDragging ? "rotate(5deg)" : "none",
                boxShadow: isDragging ? "0 5px 15px rgba(0,0,0,0.3)" : "0 2px 5px rgba(0,0,0,0.1)"
            }}>
                {/* Drag Handle */}
                <div
                    {...listeners}
                    style={{
                        position: "absolute",
                        top: "5px",
                        left: "5px",
                        background: "rgba(0,0,0,0.7)",
                        color: "white",
                        padding: "2px 6px",
                        fontSize: "10px",
                        borderRadius: "3px",
                        cursor: "grab",
                        zIndex: 10,
                    }}
                >
                    ⋮⋮ {index + 1}
                </div>

                {/* Media Content */}
                {mediaType === 'image' ? (
                    <img
                        src={mediaUrl}
                        alt={`Media ${index + 1}`}
                        style={{ width: "120px", height: "120px", objectFit: "cover", display: "block" }}
                    />
                ) : mediaType === 'video' ? (
                    <video
                        src={mediaUrl}
                        style={{ width: "120px", height: "120px", objectFit: "cover", display: "block" }}
                        controls={false}
                        muted
                    />
                ) : (
                    <div style={{ 
                        width: "120px", 
                        height: "120px", 
                        backgroundColor: "#f0f0f0", 
                        display: "flex", 
                        alignItems: "center", 
                        justifyContent: "center",
                        border: "1px solid #ccc"
                    }}>
                        Invalid Media
                    </div>
                )}

                {/* Media Type Badge */}
                <div style={{
                    position: "absolute",
                    bottom: "0",
                    left: "0",
                    right: "0",
                    background: "rgba(0,0,0,0.7)",
                    color: "white",
                    padding: "2px",
                    fontSize: "10px",
                    textAlign: "center"
                }}>
                    {mediaType.toUpperCase()}
                </div>

                {/* Remove Button */}
                <button
                    type="button"
                    onClick={() => onRemove(index)}
                    style={{
                        position: "absolute",
                        top: "5px",
                        right: "5px",
                        background: "red",
                        color: "white",
                        border: "none",
                        borderRadius: "50%",
                        width: "24px",
                        height: "24px",
                        fontSize: "14px",
                        cursor: "pointer",
                        zIndex: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    ×
                </button>
            </div>
        </div>
    );
};

const ProductForm = ({submitEvent,loading,detail=null,value}) => {

    //fetch collection to set collection in product
    const [page, setPage] = useState(1);
    const [limit] = useState(10); 
    const {data,error, isLoading} = useListAllQuery({ page, limit })
    if(isLoading) <LoadingComponent/>
    const collectionList = data?.result

    const sizeDTO = Yup.object({
        size: Yup.string().min(1).max(5).required(), // e.g., "M", "L", etc.
        quantity: Yup.number().integer().min(0).required() // quantity as a non-negative integer
    });
    const productDTO = Yup.object({
        title:Yup.string().min(3).max(50).required('Title is required'),
        description:Yup.string().required().nullable().notRequired(),
        fit:Yup.string().nullable().notRequired(),
        modelSize:Yup.string().nullable().notRequired(),
        materialCare:Yup.string().nullable().notRequired(),
        price: Yup.number()
        .transform((value, originalValue) => (originalValue === "" ? undefined : value)) // Handle empty string
        .min(0, "Price must be greater than or equal to 0.")
        .required("Price is required."),
        cost: Yup.number()
        .transform((value, originalValue) => (originalValue === "" ? undefined : value)) // Handle empty string
        .min(0, "Cost Price must be greater than or equal to 0.")
        .required("Cost Price is required."),
        sizes:Yup.array('Must add at least one size').of(sizeDTO).min(1, 'At least one size is required').required(),
        wearable:Yup.object({
            label:Yup.string().matches(/^(Summer|Winter|Both)$/).nullable().notRequired(),
            value:Yup.string().matches(/^(Summer|Winter|Summer and Winter)$/).nullable().notRequired()
        }).nullable().notRequired(),
        productCollections:Yup.array().required().nullable().notRequired().default([]),
        media: Yup.array().of(
            Yup.mixed().test('file-type', 'Only images and videos are allowed', (value) => {
                if (!value) return true; // Allow null/undefined
                if (typeof value === 'string') return true; // Allow URLs (for existing media)
                if (value instanceof File) {
                    return value.type.startsWith('image/') || value.type.startsWith('video/');
                }
                if (typeof value === 'object' && value.url) {
                    return true; // Allow existing media objects with url property
                }
                return false;
            })
        ).nullable().notRequired(),  
    })
    const {control, handleSubmit,register, setValue,getValues, formState:{errors} } = useForm({
        resolver: yupResolver(productDTO),
        defaultValues: {
            sizes: [{ size: '', quantity: 0 }]
        }
    })
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'sizes'
    });

    useEffect(()=>{
        if(detail){
            setValue("title", detail.title)
            setValue("description", detail.description)
            setValue("fit", detail.fit)
            setValue("materialCare", detail.materialCare)
            setValue("modelSize", detail.modelSize)
            setValue("price", detail.price)
            setValue("cost", detail.cost)
            setValue("wearable", detail.wearable)
            // Convert separate images and video to single media array for backward compatibility
            const mediaArray = [];
            if (detail.images) {
                detail.images.forEach(img => {
                    mediaArray.push({
                        url: typeof img === 'string' ? img : img.url,
                        type: 'image',
                        position: typeof img === 'object' ? img.position : mediaArray.length,
                        alt: typeof img === 'object' ? img.alt : null
                    });
                });
            }
            if (detail.video) {
                mediaArray.push({
                    url: detail.video,
                    type: 'video',
                    position: mediaArray.length,
                    alt: null
                });
            }
            // Handle new media format
            if (detail.media) {
                setValue("media", detail.media);
            } else if (mediaArray.length > 0) {
                setValue("media", mediaArray);
            }
            
            // Handle sizes - replace default empty field with actual sizes
            if (detail.sizes && detail.sizes.length > 0) {
                // Clear all existing fields first
                setValue("sizes", []);
                // Then set the actual sizes from the product
                setValue("sizes", detail.sizes);
            }        
            // Initialize collections
            if (detail.productCollections) {
            setValue("productCollections", detail.productCollections);
            }
        }
    },[detail, setValue])

    // Drag and Drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleMediaUpload = (e) => {
        const files = Array.from(e.target.files);
        const existingMedia = getValues("media") || [];
        setValue("media", [...existingMedia, ...files]);
    };

    // Watch media changes for real-time updates
    const watchedMedia = useWatch({
        control,
        name: "media",
        defaultValue: []
    });

    const removeMedia = (index) => {
        const media = getValues("media") || [];
        const updatedMedia = media.filter((_, i) => i !== index);
        setValue("media", updatedMedia, { shouldDirty: true });
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (active.id !== over?.id && over) {
            const media = watchedMedia || [];
            const activeIndex = parseInt(active.id.replace('media-', ''));
            const overIndex = parseInt(over.id.replace('media-', ''));

            if (activeIndex !== overIndex && !isNaN(activeIndex) && !isNaN(overIndex)) {
                const reorderedMedia = arrayMove(media, activeIndex, overIndex);
                setValue("media", reorderedMedia, { shouldDirty: true });
            }
        }
    };    

  return (
    <form onSubmit={handleSubmit(submitEvent)}>
        <h3>Content</h3>
        <div className="from_grid">
            <div>
                <label htmlFor="title">Product Title</label><br />
                <TextInputComponent
                    name='title'
                    control={control}
                    type='text'
                    defaultValue=''
                    errMsg={errors?.title?.message}
                    required:true
                />
            </div>
            <div>
                <label htmlFor="description">Description</label><br />
                <DescriptionInput
                    name='description'
                    control={control}
                    type='text'
                    defaultValue=''
                    errMsg={errors?.description?.message}
                />
            </div>
            
        </div>
        <h3>Details</h3>
        <div className="from_grid">
            <div>
                <label htmlFor="price">Price</label><br />
                <TextInputComponent
                    name='price'
                    control={control}
                    type='text'
                    defaultValue=''
                    errMsg={errors?.price?.message}
                />
            </div>
            <div>
                <label htmlFor="cost">Cost Price</label><br />
                <TextInputComponent
                    name='cost'
                    control={control}
                    type='text'
                    defaultValue=''
                    errMsg={errors?.cost?.message}
                />
            </div>
            <div>
                <label htmlFor="pattern">Wearable</label><br />
                <WearableOptionsCompoent
                    name='wearable'
                    control={control}
                    errMsg={errors?.wearable?.message}
                />
            </div>
            
        </div>
        <h3 style={{marginTop:'30px'}}>Sizes</h3>
        <div className="from_grid">
            {fields.map((item, index) => (
                <div key={item.id} style={{ marginBottom: '10px' }}>
                    <label>
                        Size:
                        <input style={{padding:'10px'}}
                            {...register(`sizes.${index}.size`)}
                            placeholder="Size (e.g., M, L)"
                        />
                        {errors.sizes?.[index]?.size && <p style={{ color: 'red' }}>{errors.sizes[index].size.message}</p>}
                    </label>

                    <label>
                        Quantity:
                        <input
                            {...register(`sizes.${index}.quantity`)}
                            placeholder="Quantity"
                        />
                        {errors.sizes?.[index]?.quantity && <p style={{ color: 'red' }}>{errors.sizes[index].quantity.message}</p>}
                    </label>

                    <button type="button" onClick={() => remove(index)} style={{background:'#d74747', padding:'5px 10px', borderRadius:'5px'}}>
                        Remove Size
                    </button>
                </div>
            ))}

            <button type="button" onClick={() => append({ size: '', quantity: 0 })} style={{background:'#babaeb', padding:'5px 10px', borderRadius:'5px'}}>
                Add Another Size
            </button>
        </div>
        <h3 style={{marginTop:'30px'}}>Other Details</h3>
        <div className="from_grid">            
            
            <div>
                <label htmlFor="productCollections">Product Collections</label>
                <Controller
                    name="productCollections"
                    control={control}
                    render={({ field }) => (
                    <Select
                        {...field}
                        options={collectionList? collectionList.map((collection) => ({
                        value: collection._id,
                        label: collection.name,
                        })) 
                        : []
                    }

                        isMulti
                        placeholder="Select collections"
                    />
                    )}
                />
                {errors?.productCollections && (
                    <span style={{ color: "red", fontStyle: "italic" }}>
                    {errors.productCollections.message}
                    </span>
                )}
            </div>
            <div>
                <label htmlFor="fit">Fit</label><br />
                <TextInputComponent
                    name='fit'
                    control={control}
                    type='text'
                    defaultValue=''
                    errMsg={errors?.fit?.message}
                />
            </div>
            <div>
                <label htmlFor="modelSize">Model Size</label><br />
                <TextInputComponent
                    name='modelSize'
                    control={control}
                    type='text'
                    defaultValue=''
                    errMsg={errors?.modelSize?.message}
                />
            </div>
            <div>
                <label htmlFor="materialCare">Material and care</label><br />
                <DescriptionInput
                    name='materialCare'
                    control={control}
                    type='text'
                    defaultValue=''
                    errMsg={errors?.materialCare?.message}
                />
            </div>
        </div>
        <h3 style={{marginTop:'30px'}}>Media (Images & Videos)</h3>
        <div className="from_grid"> 
            <div>
                <label>Upload Media Files (Images & Videos)</label>
                <input 
                    type="file" 
                    multiple 
                    accept="image/*,video/*" 
                    onChange={handleMediaUpload} 
                />
                <div className="media-preview" style={{ marginTop: "15px" }}>
                    <p style={{ fontSize: "14px", color: "#666", marginBottom: "10px" }}>
                        Drag and drop to reorder media. Click × to remove items.
                    </p>
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={(watchedMedia || []).map((_, index) => `media-${index}`)}
                            strategy={rectSortingStrategy}
                        >
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                                {(watchedMedia || []).map((mediaItem, index) => (
                                    <SortableMediaItem
                                        key={`media-${index}`}
                                        mediaItem={mediaItem}
                                        index={index}
                                        onRemove={removeMedia}
                                    />
                                ))}
                            </div>
                        </SortableContext>
                    </DndContext>
                    {(!watchedMedia || watchedMedia.length === 0) && (
                                    <div style={{ 
                            padding: "20px", 
                            border: "2px dashed #ddd", 
                            borderRadius: "8px", 
                            textAlign: "center",
                            color: "#666",
                            marginTop: "10px"
                        }}>
                            No media files uploaded yet. Use the file input above to add images and videos.
                                    </div>
                                )}
                    {errors?.media && (
                        <div style={{ color: 'red', marginTop: '10px', fontSize: '14px' }}>
                            Error: {errors.media.message || 'Media validation failed'}
                        </div>
                    )}
                </div>
            </div>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'center',  }}>            
            <input className='submit_btn' type="submit" value={value} disabled={loading} style={{cursor:'pointer'}}/>
        </div>
    </form>
  )
}

export default ProductForm